import {HelpTooltip} from 'components/system/help_tooltip';
import {useState} from 'react';
import {QuestionListOrdering, QuestionListOrderingDirection} from 'types/questions';
import {defaultQuestionListConfig} from 'utils/question_list';
import {QuestionRow} from './question_row';
import styles from './question_table.module.css';

export const QuestionTable = ({questions, onQuestionChange, config, onConfigChange}: Props) => {
  const [hoverQuestionSlug, setHoverQuestionSlug] = useState('');
  return (
    <div className={styles.wrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope='col'>
              <OrderBy
                label='Title'
                orderKey={QuestionListOrdering.Title}
                firstDirection={QuestionListOrderingDirection.ASC}
                config={config}
                onConfigChange={onConfigChange}
              />
            </th>
            <th scope='col'>
              <OrderBy
                label='Score'
                orderKey={QuestionListOrdering.Score}
                firstDirection={QuestionListOrderingDirection.DESC}
                config={config}
                onConfigChange={onConfigChange}
              />
            </th>
            <th scope='col'>Tags</th>
            <th scope='col'>Company</th>
            <th scope='col'>
              <OrderBy
                label='Difficulty'
                orderKey={QuestionListOrdering.Difficulty}
                firstDirection={QuestionListOrderingDirection.ASC}
                config={config}
                onConfigChange={onConfigChange}
              />
            </th>
            <th scope='col'>
              <OrderBy
                label={
                  <div className={styles.tooltipHeader}>
                    Success <HelpTooltip type='SUCCESS' />
                  </div>
                }
                orderKey={QuestionListOrdering.SuccessRate}
                firstDirection={QuestionListOrderingDirection.DESC}
                config={config}
                onConfigChange={onConfigChange}
              />
            </th>
            <td></td>
          </tr>
        </thead>
        <tbody>
          {questions.map((question, ranking) => (
            <QuestionRow
              key={question.uuid}
              showPreview={question.slug === hoverQuestionSlug}
              onChangePreview={(slug) => setHoverQuestionSlug(slug)}
              question={question}
              onQuestionChange={onQuestionChange}
              config={config}
              onConfigChange={onConfigChange}
              ranking={ranking}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

type Props = {
  questions: Question[];
  onQuestionChange: (question: Question) => void;
  config: QuestionListConfig;
  onConfigChange: (config: QuestionListConfig) => void;
};

const OrderBy = ({label, orderKey, firstDirection, config, onConfigChange}: OrderByProps) => {
  return (
    <div
      className={styles.orderBy}
      onClick={() => {
        if (config.ordering !== orderKey) {
          onConfigChange({
            ...config,
            ordering: orderKey,
            orderingDirection: firstDirection,
          });
        } else {
          if (config.orderingDirection === firstDirection) {
            onConfigChange({
              ...config,
              ordering: orderKey,
              orderingDirection:
                firstDirection === QuestionListOrderingDirection.ASC
                  ? QuestionListOrderingDirection.DESC
                  : QuestionListOrderingDirection.ASC,
            });
          } else {
            onConfigChange({
              ...config,
              ordering: defaultQuestionListConfig.ordering,
              orderingDirection: defaultQuestionListConfig.orderingDirection,
            });
          }
        }
      }}>
      {label}
      <OrderByArrows
        activeDirection={config.ordering === orderKey ? config.orderingDirection : undefined}
      />
    </div>
  );
};

type OrderByProps = {
  label: React.ReactNode;
  config: QuestionListConfig;
  orderKey: QuestionListOrdering;
  firstDirection: QuestionListOrderingDirection;
  onConfigChange: (config: QuestionListConfig) => void;
};

const OrderByArrows = ({activeDirection}: OrderByArrowsProps) => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 24 24'
      width='1em'
      height='1em'
      fill='currentColor'>
      <path
        opacity={activeDirection === QuestionListOrderingDirection.ASC ? 1 : 0.3}
        d='M18.695 9.378L12.83 3.769a1.137 1.137 0 00-.06-.054c-.489-.404-1.249-.377-1.7.06L5.303 9.381a.51.51 0 00-.16.366c0 .297.27.539.602.539h12.512a.64.64 0 00.411-.146.501.501 0 00.028-.762z'
      />
      <path
        opacity={activeDirection === QuestionListOrderingDirection.DESC ? 1 : 0.3}
        d='M12.77 20.285c.021-.017.042-.035.062-.054l5.863-5.609a.5.5 0 00-.028-.762.64.64 0 00-.41-.146H5.743c-.332 0-.601.242-.601.54a.51.51 0 00.16.365l5.769 5.606c.45.437 1.21.464 1.698.06z'
      />
    </svg>
  );
};

type OrderByArrowsProps = {
  activeDirection?: QuestionListOrderingDirection;
};
